import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AuthContext } from "../../../context/AuthContext";
import { useBooking } from "../../../context/BookingContext";
import CloseIcon from "@mui/icons-material/Close";
import RenterTermsModal from "../../../components/modals/modalVariants/RenterTermsModal";
import DocVerificationModal from "../../../components/modals/modalVariants/DocVerificationModal";
import AuthModal from "../../../components/modals/AuthModal/AuthModal";

import moment from "moment";
import { useNavigate } from "react-router-dom";

const KitchenBooking = ({
  kitchen,
  kitchenID,
  checkinDate,
  setCheckinDate,
  checkoutDate,
  setCheckoutDate,
  totalAmount,
  setTotalAmount,
  isCheckingAvailability,
  setIsCheckingAvailability,
  availabilityStatus,
  setAvailabilityStatus,
}) => {
  const { user, loading } = useContext(AuthContext);
  const { initiateStripePayment, checkKitchenAvailability } = useBooking();
  const navigate = useNavigate();

  const [agreed, setAgreed] = useState(false);
  const [matchedRate, setMatchedRate] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [showRenterTermsModal, setShowRenterTermsModal] = useState(false);
  const [showDocVerificationModal, setShowDocVerificationModal] = useState(false);
  const [missingDocs, setMissingDocs] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const openAuthModal = (mode = "login") => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };
  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  const toggleRenterTermsModal = () => setShowRenterTermsModal((prev) => !prev);
  const toggleDocVerificationModal = () =>
    setShowDocVerificationModal((prev) => !prev);

  const computeEffectiveIntervalForRecurring = (bookingStart, slot) => {
    const slotStart = moment.utc(slot.startDateTime);
    const slotEnd = moment.utc(slot.endDateTime);
    const bookingWeekday = moment(bookingStart).local().weekday();
    const slotStartDay = moment(slot.startDateTime).local().weekday();
    const daysToSubtract = (bookingWeekday - slotStartDay + 7) % 7;
    const alignedStartDate = moment(bookingStart).subtract(
      daysToSubtract,
      "days"
    );

    const effectiveStart = alignedStartDate
      .clone()
      .set({
        hour: slotStart.hour(),
        minute: slotStart.minute(),
        second: 0,
        millisecond: 0,
      })
      .utc();

    const durationMs = slot.duration || slotEnd.diff(slotStart);
    const effectiveEnd = moment.utc(effectiveStart).add(durationMs);
    return { effectiveStart, effectiveEnd };
  };

  const validateBookingSlot = (
    bookingStart,
    bookingEnd,
    slot,
    toleranceHours = 1
  ) => {
    const toleranceMs = moment
      .duration(toleranceHours, "hours")
      .asMilliseconds();
    let effectiveStart, effectiveEnd;

    if (slot.recurring) {
      ({ effectiveStart, effectiveEnd } = computeEffectiveIntervalForRecurring(
        bookingStart,
        slot
      ));
    } else {
      effectiveStart = moment.utc(slot.startDateTime);
      effectiveEnd = moment.utc(slot.endDateTime);
    }

    if (
      bookingStart.isSameOrAfter(effectiveStart) &&
      bookingEnd.isSameOrBefore(effectiveEnd)
    ) {
      return { status: "exact", effectiveStart, effectiveEnd };
    }

    if (
      Math.abs(bookingStart.diff(effectiveStart)) <= toleranceMs ||
      Math.abs(bookingEnd.diff(effectiveEnd)) <= toleranceMs
    ) {
      return { status: "near", effectiveStart, effectiveEnd };
    }

    return { status: "no", effectiveStart, effectiveEnd };
  };

  const findAvailableSlot = (kitchen, bookingStart, bookingEnd) => {
    let matchedEntry = null;
    let nearMatch = null;

    for (let slot of kitchen.RateEntries || []) {
      const { status, effectiveStart, effectiveEnd } = validateBookingSlot(
        bookingStart,
        bookingEnd,
        slot
      );
      if (status === "exact") {
        matchedEntry = { slot, effectiveStart, effectiveEnd };
        break;
      } else if (status === "near" && !nearMatch) {
        nearMatch = { slot, effectiveStart, effectiveEnd };
      }
    }

    return { matchedEntry, nearMatch };
  };

  const findClosestSlot = (kitchen, bookingStart, bookingEnd) => {
    let closestSlot = null;
    let minDistance = Infinity;

    for (let slot of kitchen.RateEntries || []) {
      let effectiveStart, effectiveEnd;
      if (slot.recurring) {
        ({ effectiveStart, effectiveEnd } =
          computeEffectiveIntervalForRecurring(bookingStart, slot));
      } else {
        effectiveStart = moment.utc(slot.startDateTime);
        effectiveEnd = moment.utc(slot.endDateTime);
      }

      const distance = Math.min(
        Math.abs(bookingStart.diff(effectiveStart)),
        Math.abs(bookingEnd.diff(effectiveEnd))
      );

      if (distance < minDistance) {
        minDistance = distance;
        closestSlot = { effectiveStart, effectiveEnd };
      }
    }

    return {
      suggestion: closestSlot
        ? `${closestSlot.effectiveStart
            .local()
            .format("dddd, h:mm A")} - ${closestSlot.effectiveEnd
            .local()
            .format("dddd, h:mm A")}`
        : null,
    };
  };

  const meetsMinBookingRequirement = () => {
    const min = parseFloat(kitchen.MinBooking || 0);
    const durationHours = moment(checkoutDate).diff(
      moment(checkinDate),
      "hours",
      true
    );
    if (durationHours < min) {
      setAvailabilityStatus({
        message: `Minimum booking requirement is ${min} hour(s), but you selected ${durationHours.toFixed(
          2
        )} hour(s).`,
        type: "error",
      });
      return false;
    }
    return true;
  };

  const validateUserForBooking = () => {
    const missing = [];
    if (user.idVerificationStatus !== "verified") {
      missing.push("ID Verification");
    }
    if (!user.documents?.foodHandler) {
      missing.push("Food Handler Certificate");
    }
    if (!user.documents?.insurance) {
      missing.push("Insurance Document");
    }
    if (missing.length > 0) {
      setMissingDocs(missing);
      setShowDocVerificationModal(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!checkinDate || !checkoutDate) return;

    const bookingStart = moment.utc(checkinDate);
    const bookingEnd = moment.utc(checkoutDate);
    const duration = bookingEnd.diff(bookingStart, "hours", true);

    if (duration <= 0) {
      setAvailabilityStatus({ message: "Invalid time range.", type: "error" });
      setTotalHours(0);
      setMatchedRate(0);
      setTotalAmount(0);
      return;
    }

    const { matchedEntry, nearMatch } = findAvailableSlot(
      kitchen,
      bookingStart,
      bookingEnd
    );

    if (matchedEntry) {
      const price = parseFloat(matchedEntry.slot.price);
      setMatchedRate(price);
      setTotalHours(duration);
      setTotalAmount(price * duration);
      setAvailabilityStatus({ message: "Kitchen available!", type: "success" });
    } else {
      const { suggestion } = nearMatch
        ? {
            suggestion: `Adjust to ${nearMatch.effectiveStart
              .local()
              .format("dddd, h:mm A")} - ${nearMatch.effectiveEnd
              .local()
              .format("dddd, h:mm A")}`,
          }
        : findClosestSlot(kitchen, bookingStart, bookingEnd);

      setMatchedRate(0);
      setTotalHours(0);
      setTotalAmount(0);
      setAvailabilityStatus({
        message: nearMatch
          ? "Booking is within 1 hour of an available slot."
          : "No availability matches your selection.",
        type: nearMatch ? "warning" : "error",
        suggestion,
      });
    }
  }, [checkinDate, checkoutDate, kitchen]);

  const handleBooking = async () => {
    if (!user) {
      setAvailabilityStatus({});
      setAvailabilityStatus({
        message: "Please login to continue.",
        type: "error",
      });
      openAuthModal("login");
      return;
    }

    if (loading) {
      setAvailabilityStatus({
        message: "Loading user data, please wait...",
        type: "info",
      });
      return;
    }

    if (!agreed) {
      setAvailabilityStatus({
        message: "Please agree to the Terms and Conditions.",
        type: "error",
      });
      return;
    }

    if (!checkinDate || !checkoutDate) {
      setAvailabilityStatus({
        message: "Please select check-in and check-out dates.",
        type: "error",
      });
      return;
    }

    if (checkoutDate <= checkinDate) {
      setAvailabilityStatus({
        message: "Check-out time must be after check-in time.",
        type: "error",
      });
      return;
    }

    if (!meetsMinBookingRequirement()) {
      setAvailabilityStatus({
        message: "Does not match minimum booking requirement.",
        type: "error",
      });
      return;
    }

    if (!validateUserForBooking()) {
      setAvailabilityStatus({
        message: "Please verify your documents.",
        type: "error",
      });
      return;
    }

    const bookingStart = moment.utc(checkinDate);
    const bookingEnd = moment.utc(checkoutDate);
    const formattedCheckin = bookingStart.toISOString();
    const formattedCheckout = bookingEnd.toISOString();

    setIsCheckingAvailability(true);

    try {
      const { available } = await checkKitchenAvailability(
        kitchenID,
        formattedCheckin,
        formattedCheckout
      );
      setIsCheckingAvailability(false);

      if (!available) {
        setAvailabilityStatus({
          message: "Kitchen not available.",
          type: "error",
        });
        return;
      }

      const base = totalAmount;
      const service = base * 0.15;
      const deposit = parseFloat(kitchen.SecurityDeposit) || 0;
      const finalTotal = base + service + deposit;

      const bookingDetails = {
        kitchenID,
        kitchenOwnerID: kitchen.UserID,
        userID: user.userID,
        kitchenTitle: kitchen.Title,
        bookingType: "hour",
        totalAmount: finalTotal,
        baseAmount: base,
        serviceFee: service,
        securityDeposit: deposit,
        checkinDate: formattedCheckin,
        checkoutDate: formattedCheckout,
        totalHours,
      };

      await initiateStripePayment(bookingDetails);
    } catch (err) {
      setIsCheckingAvailability(false);
      setAvailabilityStatus({
        message: `Booking failed: ${err.message || "Please try again."}`,
        type: "error",
      });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Hourly Rate: ${kitchen.HourlyRate || "N/A"}
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          <DateTimePicker
            label="Check-In Date and Time"
            value={checkinDate}
            onChange={(val) => {
              setCheckinDate(val);
              setCheckoutDate(null);
              setAvailabilityStatus({
                message: "",
                type: "",
                suggestion: null,
              });
            }}
            minDate={new Date()}
          />

          <DateTimePicker
            label="Check-Out Date and Time"
            value={checkoutDate}
            onChange={setCheckoutDate}
            disabled={!checkinDate}
            minDate={checkinDate || new Date()}
          />

          {availabilityStatus.message && (
            <Alert
              severity={availabilityStatus.type}
              action={
                <IconButton
                  onClick={() =>
                    setAvailabilityStatus({
                      message: "",
                      type: "",
                      suggestion: null,
                    })
                  }
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {availabilityStatus.message}
              {availabilityStatus.suggestion && (
                <div style={{ fontSize: "0.875rem" }}>
                  <em>Suggestion:</em> {availabilityStatus.suggestion}
                </div>
              )}
            </Alert>
          )}

          {totalAmount > 0 && (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      Amount ({totalHours.toFixed(2)} hrs × $
                      {matchedRate.toFixed(2)})
                    </TableCell>
                    <TableCell>${totalAmount.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Taxes & Fees</TableCell>
                    <TableCell>${(totalAmount * 0.15).toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Security Deposit (*refundable)</TableCell>
                    <TableCell>
                      ${(parseFloat(kitchen.SecurityDeposit) || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell>
                      <strong>
                        $
                        {(
                          totalAmount +
                          totalAmount * 0.15 +
                          (parseFloat(kitchen.SecurityDeposit) || 0)
                        ).toFixed(2)}
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
            }
            label={
              <Typography
                onClick={toggleRenterTermsModal}
                sx={{ cursor: "pointer" }}
              >
                I agree to the <strong>Terms and Conditions</strong>
              </Typography>
            }
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleBooking}
            disabled={!agreed || isCheckingAvailability || !totalAmount}
            sx={{ borderRadius: 2 }}
          >
            {isCheckingAvailability ? (
              <CircularProgress size={24} />
            ) : (
              "Book Now"
            )}
          </Button>
        </Box>
      </Paper>
      <RenterTermsModal
        open={showRenterTermsModal}
        onClose={toggleRenterTermsModal}
      />
      <DocVerificationModal
        open={showDocVerificationModal}
        onClose={toggleDocVerificationModal}
        missingDocs={missingDocs}
        onNavigate={() => {
          toggleDocVerificationModal();
          navigate("/profile/settings");
        }}
      />
      <AuthModal
        open={showAuthModal}
        onClose={closeAuthModal}
        initialMode={authMode}
      />
    </LocalizationProvider>
  );
};

export default KitchenBooking;
